import React from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle, Lock, Sparkles, Unlock, LockKeyhole, Wallet, ArrowDownCircle, ArrowUpCircle, Ban,
  FileMinus, PackageX, FileWarning, ShieldAlert, Moon, ShoppingBag, Flame, Loader2, Scale,
} from "lucide-react";

export const clp = (n) => {
  const v = Math.round(Number(n || 0));
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString("es-CL")}`;
};

export const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", hour12: false }) : "—");

export const hace = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "ahora";
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("es-CL", { day: "2-digit", month: "short" });
};

export const saludo = () => {
  const h = Number(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", hour12: false }).slice(0, 2));
  return h < 12 ? "Buenos días" : h < 20 ? "Buenas tardes" : "Buenas noches";
};

const P = {
  amber: "border-amber-500/40 text-amber-400 bg-amber-500/10",
  emerald: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
  rose: "border-rose-500/40 text-rose-400 bg-rose-500/10",
  cyan: "border-cyan-500/40 text-cyan-300 bg-cyan-500/10",
  violet: "border-violet-500/40 text-violet-300 bg-violet-500/10",
};

export const TIPO_UI = {
  APERTURA_CAJA: { icon: Unlock, pal: "cyan", label: "APERTURA" },
  CIERRE_CAJA: { icon: LockKeyhole, pal: "emerald", label: "CIERRE DE CAJA" },
  CUADRATURA: { icon: Scale, pal: "rose", label: "CUADRATURA" },
  EGRESO_CAJA: { icon: ArrowUpCircle, pal: "rose", label: "EGRESO DE CAJA" },
  INGRESO_CAJA: { icon: ArrowDownCircle, pal: "cyan", label: "INGRESO" },
  ANULACION: { icon: Ban, pal: "rose", label: "ANULACIÓN" },
  NOTA_CREDITO: { icon: FileMinus, pal: "amber", label: "NOTA DE CRÉDITO" },
  STOCK_CRITICO: { icon: PackageX, pal: "amber", label: "STOCK CRÍTICO" },
  FOLIOS_BAJOS: { icon: FileWarning, pal: "amber", label: "FOLIOS DTE" },
  RECHAZO_SII: { icon: ShieldAlert, pal: "rose", label: "RECHAZO SII" },
  RESUMEN_DIARIO: { icon: Moon, pal: "emerald", label: "CIERRE DEL DÍA" },
  PEDIDO_ONLINE: { icon: ShoppingBag, pal: "violet", label: "PEDIDO ONLINE" },
  VENTA_GRANDE: { icon: Flame, pal: "emerald", label: "VENTA GRANDE" },
};
export const pal = (k) => P[k] || P.cyan;

export const Glass = ({ className = "", children, ...p }) => (
  <div className={`rounded-3xl border border-white/[0.08] bg-[#0B1324] ${className}`} {...p}>{children}</div>
);

export const Rise = ({ i = 0, children, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }} className={className}>
    {children}
  </motion.div>
);

export const SectionTitle = ({ children, right }) => (
  <div className="flex items-center justify-between mt-7 mb-3 px-1">
    <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{children}</h2>
    {right}
  </div>
);

export const ProBadge = ({ tier }) => tier === "PRO" ? (
  <span data-testid="mobile-plan-badge" className="font-mono text-[10px] font-bold tracking-widest px-2.5 py-0.5 rounded-full uppercase text-amber-200 border border-amber-400/50 bg-gradient-to-r from-amber-500/20 via-cyan-500/15 to-amber-500/20 shadow-[0_0_14px_rgba(251,191,36,0.25)]">
    Pro
  </span>
) : (
  <span data-testid="mobile-plan-badge" className="font-mono text-[10px] font-semibold tracking-widest px-2.5 py-0.5 rounded-full uppercase text-slate-300 border border-slate-700 bg-slate-800">
    Start
  </span>
);

export const ProLock = ({ titulo, detalle, testId }) => (
  <Glass data-testid={testId || "pro-lock"} className="relative overflow-hidden p-5 border-amber-400/20">
    <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-amber-400/10 blur-2xl" />
    <div className="flex items-start gap-3 relative">
      <div className="w-10 h-10 rounded-2xl grid place-items-center bg-amber-400/10 border border-amber-400/30 text-amber-300 shrink-0">
        <Lock className="w-4 h-4" />
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-100">{titulo}</p>
        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{detalle}</p>
        <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-300">
          <Sparkles className="w-3.5 h-3.5" /> Disponible en Ai-POS Pro
        </p>
      </div>
    </div>
  </Glass>
);

export const Spinner = () => (
  <div className="py-16 grid place-items-center text-cyan-400" data-testid="mobile-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
);

export const Empty = ({ icon: I = AlertTriangle, children }) => (
  <div className="py-12 text-center text-slate-500 text-sm"><I className="w-6 h-6 mx-auto mb-2 opacity-60" />{children}</div>
);

export const AlertCard = ({ a, onClick, i = 0 }) => {
  const u = TIPO_UI[a.tipo] || TIPO_UI.APERTURA_CAJA;
  const I = u.icon;
  return (
    <Rise i={i}>
      <button type="button" onClick={onClick} data-testid={`alert-card-${a.alert_id}`}
              className={`w-full text-left rounded-2xl border bg-[#0B1324] p-4 transition-colors duration-200 hover:bg-[#0F1A30] ${pal(u.pal).split(" ")[0]}`}>
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.14em] px-2 py-0.5 rounded-full border ${pal(u.pal)}`}>
            <I className="w-3 h-3" /> {u.label}
          </span>
          <span className="text-[10px] text-slate-500 font-mono flex items-center gap-1.5">
            {!a.leida && <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_#22D3EE]" />}{hace(a.created_at)}
          </span>
        </div>
        <p className="mt-2 text-[15px] font-semibold text-slate-100 leading-snug">{a.titulo}</p>
        <p className="mt-1 text-xs text-slate-400 leading-relaxed">{a.cuerpo}</p>
        {a.store_nombre && <p className="mt-2 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Wallet className="w-3 h-3" />{a.store_nombre}</p>}
      </button>
    </Rise>
  );
};
